import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useGameStore } from './game.store';

export interface StaffUser {
  id: string;
  gamertag: string;        // handle Recube (= pseudo)
  xuid: string;            // sub Recube
  role: StaffRole;
  displayRole?: string;    // nom du/des grade(s) panel (ou "Administrateur")
  permissions: Permission[];
  avatarUrl?: string;
  isAdmin?: boolean;       // admin panel (bootstrap ou grade admin) → accès total
  gradeIds?: string[];     // grades attribués (un user peut en cumuler plusieurs)
  /** Set complet des permissions <projet>.<page> (tous projets). Le scoping par jeu actif se fait dans hasPage. */
  panelPermissions?: string[];
  manageAccess?: boolean;       // peut ouvrir « Grades & accès »
  canAssignGrades?: boolean;    // peut attribuer des grades
  assignableGradeIds?: string[]; // grades qu'il a le droit d'attribuer
}

export enum StaffRole {
  ADMIN = 'admin',
  MODERATOR = 'moderator',
  HELPER = 'helper',
  BUILDER = 'builder',
}

export enum Permission {
  // Dashboard
  VIEW_DASHBOARD = 'view_dashboard',
  VIEW_STATS = 'view_stats',

  // Players
  VIEW_PLAYERS = 'view_players',
  SEARCH_PLAYERS = 'search_players',

  // Sanctions
  VIEW_SANCTIONS = 'view_sanctions',
  CREATE_SANCTION = 'create_sanction',
  EDIT_SANCTION = 'edit_sanction',
  DELETE_SANCTION = 'delete_sanction',

  // Console
  VIEW_CONSOLE = 'view_console',
  SEND_COMMAND = 'send_command',

  // Logs
  VIEW_LOGS = 'view_logs',

  // Settings
  VIEW_SETTINGS = 'view_settings',
  EDIT_SETTINGS = 'edit_settings',
}

interface AuthState {
  user: StaffUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;

  // Actions
  setAuth: (user: StaffUser, accessToken: string, refreshToken: string) => void;
  updateUserRole: (role: StaffRole, displayRole: string) => void;
  logout: () => void;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  hasPage: (pageKey: string) => boolean;
  hasProject: (projectId: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      setAuth: (user, accessToken, refreshToken) => {
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        });
      },

      updateUserRole: (role, displayRole) => {
        const { user } = get();
        if (user) {
          set({
            user: { ...user, role, displayRole },
          });
        }
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },

      hasPermission: (permission) => {
        const { user } = get();
        if (!user) return false;
        if (user.role === StaffRole.ADMIN) return true;
        return user.permissions.includes(permission);
      },

      hasAnyPermission: (permissions) => {
        const { hasPermission } = get();
        return permissions.some((p) => hasPermission(p));
      },

      hasAllPermissions: (permissions) => {
        const { hasPermission } = get();
        return permissions.every((p) => hasPermission(p));
      },

      hasPage: (pageKey) => {
        const { user } = get();
        if (!user) return false;
        if (user.isAdmin || user.role === StaffRole.ADMIN) return true;
        // Scoping par PROJET : une page n'est accordée que si le grade la possède POUR LE JEU ACTIF.
        // (Évite qu'une perm paladium.sanctions débloque la page Sanctions sous NationsGlory.)
        const project = useGameStore.getState().activeGameId;
        return (user.panelPermissions ?? []).includes(`${project}.${pageKey}`);
      },

      // Un projet n'est visible que si le user y a AU MOINS une permission (admin = tous).
      hasProject: (projectId) => {
        const { user } = get();
        if (!user) return false;
        if (user.isAdmin || user.role === StaffRole.ADMIN) return true;
        const prefix = `${projectId}.`;
        return (user.panelPermissions ?? []).some((p) => p.startsWith(prefix));
      },
    }),
    {
      name: 'recubebe-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
